import * as React from 'react';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';

import {
  ReactFCC,
  ChevronLeftIcon,
  CloseIcon,
  DelayedFocusTrap,
} from '@notino/react-styleguide';

import { useOnEscape } from '../hooks/useOnEscape';

import {
  Shell,
  Header,
  StepIndicator,
  StepWrapper,
  Background,
  Title,
  IconButton,
  Content,
} from './styled';

export interface IModalProps {
  showBack: boolean;

  currentStep: number;
  totalSteps: number;

  onClose: (currentName: string | undefined) => void;
  onBack: (currentName: string | undefined) => void;

  /**
   * current step name, usefull for tracking
   */
  name?: string;

  title?: ({
    currentStep,
    totalSteps,
  }: {
    currentStep: number;
    totalSteps: number;
  }) => React.ReactNode;
  showProgress?: boolean;

  modalProps?: {
    background?: string;
  };
}
export const Modal: ReactFCC<IModalProps> = ({
  children,
  showBack,
  totalSteps,
  currentStep,
  onClose,
  onBack,
  name,
  showProgress = true,
  title = ({ currentStep: currStep, totalSteps: total }) =>
    currStep > 0 && total > 0 ? `${currStep} / ${total}` : null,
  modalProps = {},
}) => {
  const handleClose = React.useCallback(() => {
    onClose(name);
  }, [onClose, name]);

  useOnEscape({ onEscape: handleClose });

  return createPortal(
    <RemoveScroll enabled={true}>
      <DelayedFocusTrap isActive={true}>
        <Background onClick={handleClose}>
          <Shell
            role="dialog"
            onClick={(e) => e.stopPropagation()}
            {...modalProps}
          >
            <div>
              <Header>
                {showBack ? (
                  <IconButton
                    onClick={() => onBack(name)}
                    data-testid="back-button"
                  >
                    <ChevronLeftIcon height="20px" color="icon.primary" />
                  </IconButton>
                ) : (
                  <div style={{ width: '3.5rem' }} />
                )}

                <Title>
                  {typeof title === 'function' &&
                    title({ currentStep, totalSteps })}
                </Title>

                <IconButton onClick={handleClose}>
                  <CloseIcon height="20px" color="icon.primary" />
                </IconButton>
              </Header>

              {showProgress && (
                <StepWrapper>
                  <StepIndicator
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                  />
                </StepWrapper>
              )}
            </div>

            <Content>{children}</Content>
            <div id="multistep-modal-popups" />
          </Shell>
        </Background>
      </DelayedFocusTrap>
    </RemoveScroll>,
    document.getElementById('nushop-portal-root')
  );
};
